import {
  IoNewspaperOutline,
  IoCalendarOutline,
  IoStatsChartOutline,
  IoCheckmarkCircleOutline,
  IoSaveOutline,
  IoArrowBackOutline,
  IoCloudUploadOutline
} from 'react-icons/io5';
import FormField from '../forms/FormField';
import TextArea from '../forms/TextArea';
import DateField from '../forms/DateField';
import CustomSelect from '../forms/CustomSelect';
import CustomCheckbox from '../forms/CustomCheckbox';
import TagInput from '../forms/TagInput';
import Spinner from '../common/Spinner';
import { useEffect, useState } from 'react';
import DragDropFileUpload from '../common/DragDropFileUpload';

const JournalForm = ({
  formData,
  setFormData,
  tags,
  setTags,
  tagInput,
  setTagInput,
  errors,
  setErrors,
  isSubmitting,
  handleSubmit,
  handleCancel,
  submitButtonText = 'Save Journal',
  isEdit = false
}) => {

  const [files, setFiles] = useState({
    coverLetter: null,
  });
  const [existingFiles, setExistingFiles] = useState({
    coverLetter: formData?.coverLetter || null
  });

  useEffect(() => {
    // If initialData changes (like in edit mode when data loads),
    // update our form state
    if (formData) {
      if (isEdit) {
        setExistingFiles({
          coverLetter: formData.coverLetter || null,
        });
      }
    }
  }, [isEdit, formData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'impactFactor.value' || name === 'impactFactor.year') {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }));
    } else if (name === 'metrics.averageReviewTime' ||
      name === 'metrics.acceptanceRate' ||
      name === 'metrics.timeToPublication' ||
      name === 'metrics.articlesPerYear') {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: child === 'articlesPerYear' ? (parseInt(value) || 0) : value
        }
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (checked, name) => {
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));

    // Clear error when checkbox is changed
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const { name, files: uploadedFiles } = e.target;
    if (uploadedFiles.length > 0) {
      const file = uploadedFiles[0];

      // File validation happens in the DragDropFileUpload component now
      setFiles(prev => ({
        ...prev,
        [name]: file
      }));
      setFormData(prev => ({
        ...prev,
        coverLetter: file
      }));

      // Clear file errors
      if (errors[name]) {
        setErrors(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-8">
        {/* Section: Basic Journal Information */}
        <div className="rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="p-5 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-medical-50 dark:bg-slate-800 text-medical-700 dark:text-medical-400">
                <IoNewspaperOutline className="text-xl" />
              </div>
              <div>
                <h2 className="font-serif text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Basic Journal Information
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Provide the journal’s identity and discoverability details.
                </p>
              </div>
            </div>

            <div className="space-y-5">
              <FormField
                label="Journal Title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter the title of the journal"
                error={errors.title}
                required
              />

              <TextArea
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Provide a detailed description of the journal"
                rows={5}
                error={errors.description}
                required
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="E.g., Science, Medicine, Psychology"
                  error={errors.category}
                  required
                />

                <DateField
                  label="Published Date"
                  name="publishedDate"
                  value={formData.publishedDate}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <TagInput
                label="Tags"
                tags={tags}
                setTags={setTags}
                tagInputValue={tagInput}
                setTagInputValue={setTagInput}
                placeholder="Add tags (press Enter or comma)"
                helpText="Press Enter, comma, or tab to add a tag"
              />
            </div>
          </div>
        </div>

        {/* Section: Publication Details */}
        <div className="rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="p-5 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-medical-50 dark:bg-slate-800 text-medical-700 dark:text-medical-400">
                <IoCalendarOutline className="text-xl" />
              </div>
              <div>
                <h2 className="font-serif text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Publication Details
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Set frequency, peer review process, and access policy.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <CustomSelect
                label="Publication Frequency"
                name="publicationFrequency"
                value={formData.publicationFrequency}
                onChange={handleInputChange}
                options={[
                  { value: "Daily", label: "Daily" },
                  { value: "Weekly", label: "Weekly" },
                  { value: "Monthly", label: "Monthly" },
                  { value: "Quarterly", label: "Quarterly" },
                  { value: "Annually", label: "Annually" }
                ]}
                placeholder="Select publication frequency"
                error={errors.publicationFrequency}
                required
                icon={<IoCalendarOutline />}
              />

              <FormField
                label="Peer Review Process"
                name="peerReviewProcess"
                value={formData.peerReviewProcess}
                onChange={handleInputChange}
                placeholder="E.g., Single-blind peer review"
              />
            </div>

            <div className="mt-6 rounded-xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/40 p-4">
              <CustomCheckbox
                id="openAccess"
                name="openAccess"
                checked={formData.openAccess}
                onChange={(checked) => handleCheckboxChange(checked, 'openAccess')}
                label="Open Access Journal"
              />
            </div>
          </div>
        </div>

        {/* Section: Journal Impact & Metrics */}
        <div className="rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="p-5 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-medical-50 dark:bg-slate-800 text-medical-700 dark:text-medical-400">
                <IoStatsChartOutline className="text-xl" />
              </div>
              <div>
                <h2 className="font-serif text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Journal Impact & Metrics
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Optional indicators that help authors evaluate the journal.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <FormField
                label="Impact Factor Value"
                name="impactFactor.value"
                type="number"
                min="0"
                step="0.001"
                value={formData.impactFactor.value}
                onChange={handleInputChange}
                placeholder="E.g., 4.325"
              />

              <FormField
                label="Impact Factor Year"
                name="impactFactor.year"
                type="number"
                min="1900"
                max="2100"
                value={formData.impactFactor.year}
                onChange={handleInputChange}
                placeholder="E.g., 2025"
              />
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              <FormField
                label="Average Review Time"
                name="metrics.averageReviewTime"
                value={formData.metrics.averageReviewTime}
                onChange={handleInputChange}
                placeholder="E.g., 30 days"
              />

              <FormField
                label="Acceptance Rate"
                name="metrics.acceptanceRate"
                value={formData.metrics.acceptanceRate}
                onChange={handleInputChange}
                placeholder="E.g., 50%"
              />

              <FormField
                label="Time to Publication"
                name="metrics.timeToPublication"
                value={formData.metrics.timeToPublication}
                onChange={handleInputChange}
                placeholder="E.g., 60 days"
              />

              <FormField
                label="Articles Per Year"
                name="metrics.articlesPerYear"
                type="number"
                min="0"
                value={formData.metrics.articlesPerYear}
                onChange={handleInputChange}
                placeholder="E.g., 100"
              />
            </div>
          </div>
        </div>

        {/* Section: Submission Guidelines */}
        <div className="rounded-2xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="p-5 sm:p-8">
            <div className="flex items-start gap-3 mb-6">
              <div className="mt-0.5 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-medical-50 dark:bg-slate-800 text-medical-700 dark:text-medical-400">
                <IoCheckmarkCircleOutline className="text-xl" />
              </div>
              <div>
                <h2 className="font-serif text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                  Submission Guidelines
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Provide instructions shown to authors during submission.
                </p>
              </div>
            </div>

            <TextArea
              label="Submission Guidelines"
              name="submissionGuidelines"
              value={formData.submissionGuidelines}
              onChange={handleInputChange}
              placeholder="Enter submission guidelines, one per line"
              rows={8}
              description="Enter each guideline on a new line. These will be presented as a list to authors."
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-900 hover:bg-gray-50 dark:hover:bg-slate-800 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isEdit ? (
              <>
                <IoArrowBackOutline /> Back
              </>
            ) : (
              'Cancel'
            )}
          </button>

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-bold bg-medical-900 dark:bg-medical-600 text-white hover:bg-medical-800 dark:hover:bg-medical-500 transition shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <Spinner size="small" /> {isEdit ? 'Saving...' : 'Submitting...'}
              </>
            ) : (
              <>
                <IoSaveOutline /> {submitButtonText}
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default JournalForm;
